import { Banner, Button, DefinitionList, Modal, notify, Section, Select, StatusBadge, TextAreaField, TextField, Toggle, useConfirm } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { IntegrationBody } from "./integrationParts";
import { testLine } from "./SigninProviders";

/* Admin Center > Integrations: the three Zoho cards.
 *  - Zoho connection: ONE client (id + secret) and ONE refresh token for Desk and Books. The token comes from
 *    "Connect with Zoho" (OAuth redirect) or a pasted Self Client code; the code is exchanged on the server and
 *    never stored. Secrets are never sent back.
 *  - Zoho Desk: the Desk organization, which departments are FlexFreight's (picked from Zoho; Empire ones can't be
 *    ticked), the read-only import switch and the two-way switch.
 *  - Zoho Books: the Books organization, the auto-invoice switch and the invoice layout. */

const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
export const DCS = [
  ["com", "United States (zoho.com)"],
  ["eu", "Europe (zoho.eu)"],
  ["in", "India (zoho.in)"],
  ["com.au", "Australia (zoho.com.au)"],
  ["jp", "Japan (zoho.jp)"],
  ["ca", "Canada (zoho.ca)"],
] as const;

type ProductTest = { at: string; ok: boolean; message: string } | null;
export type ZohoConnectionView = {
  settings: { dataCenter: string };
  saved: { clientId: boolean; clientSecret: boolean; refreshToken: boolean };
  connected: boolean;
  grantedVia: "code" | "refresh_token" | "connect" | "legacy" | null;
  grantedAt: string | null;
  tests: { desk: ProductTest; books: ProductTest };
  redirectUri: string;
  scopes: { desk: string[]; deskWrite: string[]; books: string[] };
  status: "Awaiting client keys" | "Awaiting authorization" | "Connected";
};
export const CONNECTION_KEY = ["console", "integrations", "zoho"];
export const ZOHO_DESK_KEY = ["console", "integrations", "zoho-desk"];
export const ZOHO_BOOKS_KEY = ["console", "integrations", "zoho-books"];

const productLine = (t: ProductTest) => (t ? `${when.format(new Date(t.at))}: ${t.ok ? "" : "Failed. "}${t.message}` : "Never");
const viaText = (v: ZohoConnectionView["grantedVia"]) => (v === "connect" ? "Connect with Zoho" : v === "code" ? "a pasted code" : v === "refresh_token" ? "a pasted refresh token" : v === "legacy" ? "the earlier Zoho Desk keys" : "not granted yet");

/** The shared Zoho connection card. */
export function ZohoConnectionCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: CONNECTION_KEY, queryFn: () => api<ZohoConnectionView>("/v1/console/integrations/zoho") });
  const v = q.data;
  const [editing, setEditing] = useState(false);
  const [params, setParams] = useSearchParams();
  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: CONNECTION_KEY });
    void qc.invalidateQueries({ queryKey: ZOHO_DESK_KEY });
    void qc.invalidateQueries({ queryKey: ZOHO_BOOKS_KEY });
  };
  // Back from Zoho: the callback appends ?zoho=connected|tested|failed&reason=...
  const flag = params.get("zoho");
  const reason = params.get("reason");
  useEffect(() => {
    if (!flag) return;
    if (flag === "connected") notify.success("Zoho connected", reason ?? undefined);
    else if (flag === "tested") notify.error("Zoho connected, but a test failed", reason ?? undefined);
    else notify.error("Zoho didn't connect", reason ?? undefined);
    refreshAll();
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("zoho");
        next.delete("reason");
        return next;
      },
      { replace: true },
    );
  }, [flag]);
  const test = useMutation({
    mutationFn: () => api<ZohoConnectionView & { test: { ok: boolean; message: string } }>("/v1/console/integrations/zoho/test", { method: "POST" }),
    onSuccess: (r) => {
      qc.setQueryData(CONNECTION_KEY, r);
      refreshAll();
      if (r.test.ok) notify.success("Zoho connected", r.test.message);
      else notify.error("A Zoho test failed", r.test.message);
    },
    onError: (e) => notify.error("Couldn't test", message(e)),
  });
  const connect = useMutation({
    mutationFn: () => api<{ url: string }>("/v1/console/integrations/zoho/connect", { method: "POST" }),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => notify.error("Couldn't start Connect with Zoho", message(e)),
  });
  return (
    <Section
      title="Zoho"
      description="One Zoho account for Zoho Desk and Zoho Books: the API Console client and a refresh token. Keys are stored encrypted on the server and never shown again."
      actions={v ? <StatusBadge domain="integration" value={v.status} /> : null}
    >
      <IntegrationBody q={q} canManage={canManage}>
        {v ? (
          <>
            <DefinitionList
              items={[
                { term: "Data center", detail: DCS.find(([k]) => k === v.settings.dataCenter)?.[1] ?? v.settings.dataCenter },
                { term: "Client", detail: v.saved.clientId && v.saved.clientSecret ? "Client ID and secret saved" : v.saved.clientId ? "Client ID saved, secret missing" : "Not set" },
                { term: "Authorization", detail: v.saved.refreshToken ? `Refresh token saved via ${viaText(v.grantedVia)}${v.grantedAt ? ` (${when.format(new Date(v.grantedAt))})` : ""}` : "Not granted yet" },
                { term: "Zoho Desk test", detail: productLine(v.tests.desk) },
                { term: "Zoho Books test", detail: productLine(v.tests.books) },
                { term: "Redirect URI", detail: <code className="ffx-num">{v.redirectUri}</code> },
              ]}
            />
            <p className="ffa-small ffa-muted">
              Register the redirect URI above on the Zoho client (Server-based Applications) for Connect with Zoho. For a pasted code, use a Self Client instead and tick the scopes shown when you connect.
            </p>
            {canManage ? (
              <div className="ffa-btnrow">
                <Button variant="primary" disabled={!v.saved.clientId || !v.saved.clientSecret} loading={connect.isPending} onClick={() => connect.mutate()}>Connect with Zoho</Button>
                <Button onClick={() => setEditing(true)}>{v.saved.clientId ? "Client keys or paste a code" : "Set up Zoho client"}</Button>
                <Button disabled={!v.connected} loading={test.isPending} onClick={() => test.mutate()}>Test connection</Button>
              </div>
            ) : null}
          </>
        ) : null}
      </IntegrationBody>
      {editing && v ? <ZohoConnectionDialog v={v} onClose={() => setEditing(false)} onSaved={(r) => { qc.setQueryData(CONNECTION_KEY, r); refreshAll(); }} /> : null}
    </Section>
  );
}

function ZohoConnectionDialog({ v, onClose, onSaved }: { v: ZohoConnectionView; onClose: () => void; onSaved: (v: ZohoConnectionView) => void }) {
  const [dc, setDc] = useState(v.settings.dataCenter || "com");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const needKeys = !v.saved.clientId || !v.saved.clientSecret;
  const save = useMutation({
    mutationFn: () =>
      api<ZohoConnectionView & { granted: { via: string } | null; test: { ok: boolean; message: string } | null }>("/v1/console/integrations/zoho", {
        method: "PUT",
        body: { dataCenter: dc, ...(clientId.trim() ? { clientId: clientId.trim() } : {}), ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}), ...(code.trim() ? { code: code.trim() } : {}) },
      }),
    onSuccess: (r) => {
      onSaved(r);
      if (r.granted) notify.success("Zoho connected", r.test ? r.test.message : undefined);
      else if (r.test && !r.test.ok) notify.error("Saved, but a test failed", r.test.message);
      else notify.success("Zoho client saved", r.saved.refreshToken ? undefined : "Now connect: Connect with Zoho, or paste a Self Client code.");
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.details as { field?: string } | undefined)?.field === "code") setCodeError(e.message);
      else notify.error("Couldn't save", message(e));
    },
  });
  const valid = (!needKeys || (clientId.trim().length >= 10 && clientSecret.trim().length >= 10)) && (clientId.trim() === "" || clientId.trim().length >= 10) && (clientSecret.trim() === "" || clientSecret.trim().length >= 10) && (code.trim() === "" || code.trim().length >= 10);
  function submit(e: FormEvent) {
    e.preventDefault();
    if (valid) save.mutate();
  }
  const scopes = [...v.scopes.desk, ...v.scopes.deskWrite, ...v.scopes.books];
  return (
    <Modal
      open
      onClose={onClose}
      title="Zoho client and authorization"
      description="From the Zoho API Console (api-console.zoho.com). Saved keys are never shown again; leave a key blank to keep it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="ffa-zoho-conn-form" disabled={!valid} loading={save.isPending}>{code.trim() ? "Save and connect" : "Save"}</Button>
        </>
      }
    >
      <form id="ffa-zoho-conn-form" className="ffa-form" onSubmit={submit} autoComplete="off">
        <Select label="Data center" value={dc} onChange={(x) => setDc(x)} options={DCS.map(([value, label]) => ({ value, label }))} />
        <TextField label="Client ID" type="password" autoComplete="new-password" spellCheck={false} optional={!needKeys} hint={v.saved.clientId && !needKeys ? "Saved. Leave blank to keep it." : "Starts with 1000."} value={clientId} onChange={(e) => setClientId(e.target.value)} />
        <TextField label="Client secret" type="password" autoComplete="new-password" spellCheck={false} optional={!needKeys} hint={v.saved.clientSecret && !needKeys ? "Saved. Leave blank to keep it." : undefined} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
        <TextField
          label="Authorization code (Zoho API Console → Self Client → Generate Code)"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          optional
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setCodeError(null);
          }}
          error={codeError}
          hint={`Valid for 3–10 minutes, used once. Tick these scopes when generating: ${scopes.join(", ")} (Desk.tickets.CREATE and Desk.tickets.UPDATE only matter once two-way sync is on). Zoho refuses scopes the client wasn't granted. A refresh token pasted here is accepted too.`}
        />
        {v.saved.refreshToken && !code.trim() ? <p className="ffa-small ffa-muted">Already authorized via {viaText(v.grantedVia)}. Paste a new code only to re-authorize.</p> : null}
      </form>
    </Modal>
  );
}

/* --------------------------------------------------- Zoho Desk ------------------------------------------------- */

export type ZohoView = {
  settings: { dataCenter: string; zohoOrgId: string; departmentIds: string[]; writeDepartmentId: string; writeEnabled: boolean };
  saved: { clientId: boolean; clientSecret: boolean; refreshToken: boolean };
  connection: { connected: boolean; dataCenter: string };
  configured: boolean;
  enabled: boolean;
  writeEnabled: boolean;
  lastTest: { at: string; ok: boolean | null; message: string | null } | null;
  lastSync: { at: string; result: { created?: number; updated?: number; replies?: number; notes?: number; conflicts?: number; skipped?: number; errors?: string[] } | null } | null;
  status: "Awaiting Zoho connection" | "Awaiting credentials" | "Needs a passing test" | "Ready to turn on" | "Syncing" | "Two-way";
};
type Department = { id: string; name: string; empire: boolean };

export function ZohoDeskCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ZOHO_DESK_KEY, queryFn: () => api<ZohoView>("/v1/console/integrations/zoho-desk") });
  const [editing, setEditing] = useState(false);
  const { confirm, dialog } = useConfirm();
  const set = (v: ZohoView) => qc.setQueryData(ZOHO_DESK_KEY, v);
  const test = useMutation({
    mutationFn: () => api<ZohoView>("/v1/console/integrations/zoho-desk/test", { method: "POST" }),
    onSuccess: (v) => {
      set(v);
      if (v.lastTest?.ok) notify.success("Zoho Desk connected", v.lastTest.message ?? undefined);
      else notify.error("Test didn't pass", v.lastTest?.message ?? undefined);
    },
    onError: (e) => notify.error("Couldn't test", message(e)),
  });
  const toggle = useMutation({
    mutationFn: (body: { enabled?: boolean; writeEnabled?: boolean }) => api<ZohoView>("/v1/console/integrations/zoho-desk", { method: "PATCH", body }),
    onSuccess: (v, body) => {
      set(v);
      if (body.writeEnabled !== undefined) notify.success(v.writeEnabled ? "Two-way sync with Zoho Desk is on" : "Two-way sync with Zoho Desk is off");
      else notify.success(v.enabled ? "Zoho Desk sync is on" : "Zoho Desk sync is off");
      void qc.invalidateQueries({ queryKey: ["console", "tickets"] });
    },
    onError: (e) => notify.error("Couldn't change sync", message(e)),
  });
  const importKb = useMutation({
    mutationFn: () => api<{ result: { created: number; updated: number; unchanged: number; skippedOtherDepartment: number; skippedNotPublished: number; errors: string[] } }>("/v1/console/integrations/zoho-desk/import-kb", { method: "POST" }),
    onSuccess: ({ result: r }) => notify.success("Knowledge base imported", `${r.created} new and ${r.updated} updated draft${r.created + r.updated === 1 ? "" : "s"} in Knowledge base (staff-only until you publish). ${r.skippedOtherDepartment} from other departments skipped.${r.errors.length ? ` ${r.errors.length} failed.` : ""}`),
    onError: (e) => notify.error("Import failed", message(e)),
  });
  const sync = useMutation({
    mutationFn: () => api<{ result: NonNullable<NonNullable<ZohoView["lastSync"]>["result"]>; integration: ZohoView }>("/v1/console/integrations/zoho-desk/sync", { method: "POST" }),
    onSuccess: (r) => {
      set(r.integration);
      void qc.invalidateQueries({ queryKey: ["console", "tickets"] });
      notify.success("Sync finished", `${r.result.created ?? 0} new, ${r.result.updated ?? 0} updated, ${r.result.replies ?? 0} messages`);
    },
    onError: (e) => notify.error("Sync failed", message(e)),
  });
  async function flipTwoWay(on: boolean) {
    if (on && !(await confirm({ title: "Create and update tickets in Zoho Desk?", body: "Every ticket opened in the portal or the console is created in the chosen FlexFreight department, and replies, notes, tags and status changes follow. Nothing is ever written to an Empire department. Every write is audited.", confirmLabel: "Turn on two-way sync" }))) return;
    toggle.mutate({ writeEnabled: on });
  }
  const v = q.data;
  return (
    <Section
      title="Zoho Desk"
      description="Pulls tickets, customer messages and private comments from the Zoho Desk departments you pick. Only FlexFreight departments may be picked; Empire Telecom's stay out. Two-way sync (off by default) also creates and updates our tickets there."
      actions={v ? <StatusBadge domain="integration" value={v.status} /> : null}
    >
      <IntegrationBody q={q} canManage={canManage}>
        {v ? (
          <>
            {!v.connection.connected ? <Banner tone="info">Connect Zoho on the card above first.</Banner> : null}
            <DefinitionList
              items={[
                { term: "Zoho organization", detail: <span className="ffx-num">{v.settings.zohoOrgId || "Not set"}</span> },
                { term: "Departments synced", detail: v.settings.departmentIds.length ? <span className="ffx-num">{v.settings.departmentIds.join(", ")}</span> : "None" },
                { term: "New tickets go to", detail: v.settings.writeDepartmentId ? <span className="ffx-num">{v.settings.writeDepartmentId}</span> : "Not chosen" },
                { term: "Last test", detail: v.lastTest ? `${when.format(new Date(v.lastTest.at))}: ${v.lastTest.message ?? (v.lastTest.ok ? "Passed" : "Failed")}` : "Never" },
                {
                  term: "Last sync",
                  detail: v.lastSync?.result
                    ? `${when.format(new Date(v.lastSync.at))}: ${v.lastSync.result.created ?? 0} new, ${v.lastSync.result.updated ?? 0} updated, ${v.lastSync.result.replies ?? 0} messages, ${v.lastSync.result.notes ?? 0} notes${v.lastSync.result.conflicts ? `, ${v.lastSync.result.conflicts} kept console changes` : ""}${v.lastSync.result.errors?.length ? `, ${v.lastSync.result.errors.length} errors` : ""}`
                    : "Never",
                },
              ]}
            />
            {canManage ? (
              <>
                <div className="ffa-btnrow">
                  <Button onClick={() => setEditing(true)}>{v.configured ? "Change settings" : "Set up Zoho Desk"}</Button>
                  <Button disabled={!v.configured} loading={test.isPending} onClick={() => test.mutate()}>Test connection</Button>
                  <Toggle look="switch" label="Sync on" checked={v.enabled} disabled={toggle.isPending || (!v.enabled && !v.lastTest?.ok)} onChange={(on) => toggle.mutate({ enabled: on })} />
                  <Button variant="primary" disabled={!v.enabled} loading={sync.isPending} onClick={() => sync.mutate()}>Sync now</Button>
                  <Button disabled={!v.enabled} loading={importKb.isPending} onClick={() => importKb.mutate()}>Import knowledge base</Button>
                </div>
                <Toggle
                  look="switch"
                  label="Create and update tickets in Zoho Desk"
                  description={v.writeEnabled ? `On. Our tickets, replies, notes, tags and status changes go to department ${v.settings.writeDepartmentId}.` : v.enabled ? "Off. Tickets flow in from Desk only." : "Turn the sync on first."}
                  checked={v.writeEnabled}
                  disabled={toggle.isPending || (!v.writeEnabled && (!v.enabled || !v.settings.writeDepartmentId))}
                  onChange={(on) => void flipTwoWay(on)}
                />
              </>
            ) : null}
          </>
        ) : null}
      </IntegrationBody>
      {editing && v ? <ZohoDeskDialog v={v} onClose={() => setEditing(false)} onSaved={set} /> : null}
      {dialog}
    </Section>
  );
}

function ZohoDeskDialog({ v, onClose, onSaved }: { v: ZohoView; onClose: () => void; onSaved: (v: ZohoView) => void }) {
  const [org, setOrg] = useState(v.settings.zohoOrgId);
  const [picked, setPicked] = useState<string[]>(v.settings.departmentIds);
  const [writeDept, setWriteDept] = useState(v.settings.writeDepartmentId);
  const [typed, setTyped] = useState(v.settings.departmentIds.join(", "));
  const depts = useQuery({ queryKey: ["console", "integrations", "zoho-desk", "departments"], queryFn: () => api<{ departments: Department[] }>("/v1/console/integrations/zoho-desk/departments"), enabled: v.connection.connected && !!v.settings.zohoOrgId, retry: false });
  const listed = depts.data?.departments ?? null;
  const ids = listed ? picked : typed.split(/[\s,]+/).filter(Boolean);
  const write = ids.includes(writeDept) ? writeDept : (ids[0] ?? "");
  const save = useMutation({
    mutationFn: () => api<ZohoView>("/v1/console/integrations/zoho-desk", { method: "PUT", body: { dataCenter: v.connection.dataCenter, zohoOrgId: org.trim(), departmentIds: ids, ...(write ? { writeDepartmentId: write } : {}) } }),
    onSuccess: (r) => {
      onSaved(r);
      notify.success("Zoho Desk settings saved", "Sync is off until a new test passes.");
      onClose();
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const valid = /^\d{4,20}$/.test(org.trim()) && ids.length > 0 && ids.every((d) => /^\d{4,25}$/.test(d));
  return (
    <Modal
      open
      onClose={onClose}
      title="Zoho Desk settings"
      description="The Desk organization and which of its departments are FlexFreight's. Keys live on the Zoho card."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="ffa-zoho-desk-form" disabled={!valid} loading={save.isPending}>Save</Button>
        </>
      }
    >
      <form id="ffa-zoho-desk-form" className="ffa-form" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }} autoComplete="off">
        <TextField label="Zoho Desk organization ID" value={org} onChange={(e) => setOrg(e.target.value)} inputMode="numeric" spellCheck={false} hint="Zoho Desk > Setup > Developer Space > API" />
        {listed ? (
          <fieldset className="ffa-access">
            <legend className="ffa-small"><strong>FlexFreight departments to sync</strong> (Empire departments can't be picked)</legend>
            {listed.map((d) => (
              <div key={d.id} className="ffa-access__row">
                <Toggle label={`${d.name}${d.empire ? " (Empire: never synced)" : ""}`} checked={picked.includes(d.id)} disabled={d.empire} onChange={(on) => setPicked((p) => (on ? [...new Set([...p, d.id])] : p.filter((x) => x !== d.id)))} />
              </div>
            ))}
          </fieldset>
        ) : (
          <TextField label="FlexFreight department IDs" value={typed} onChange={(e) => setTyped(e.target.value)} spellCheck={false} hint={depts.isPending && depts.fetchStatus !== "idle" ? "Loading departments from Zoho…" : depts.isError ? `Departments couldn't be listed (${message(depts.error)}); enter the IDs, comma separated.` : "Comma separated. Only FlexFreight departments; never Empire Telecom. Save the organization and connect Zoho to pick them from a list."} />
        )}
        {ids.length > 1 ? (
          <Select label="New tickets go to" value={write} onChange={setWriteDept} options={ids.map((id) => ({ value: id, label: listed?.find((d) => d.id === id)?.name ?? id }))} hint="Two-way sync creates our tickets in this department." />
        ) : null}
      </form>
    </Modal>
  );
}

/* --------------------------------------------------- Zoho Books ------------------------------------------------ */

export type ZohoBooksView = {
  settings: {
    dataCenter: string;
    organizationId: string;
    autoInvoice: boolean;
    paymentTermsDays: number;
    emailInvoices: boolean;
    itemName: string;
    notes: string;
    terms: string;
    customFields: { po: string; consignee: string; shipper: string };
    emailSubject: string;
    emailBody: string;
  };
  saved: { clientId: boolean; clientSecret: boolean; refreshToken: boolean };
  connected: boolean;
  configured: boolean;
  source: "console" | "env" | "mock";
  mode: "mock" | "live";
  lastTest: { at: string; ok: boolean | null; message: string | null } | null;
  status: "Using server keys" | "Awaiting Zoho connection" | "Awaiting organization" | "Needs a passing test" | "Connected" | "Auto-invoicing" | "Mock";
};

export function ZohoBooksCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ZOHO_BOOKS_KEY, queryFn: () => api<ZohoBooksView>("/v1/console/integrations/zoho-books") });
  const v = q.data;
  const [org, setOrg] = useState<string | null>(null);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const { confirm, dialog } = useConfirm();
  const set = (r: ZohoBooksView) => qc.setQueryData(ZOHO_BOOKS_KEY, r);
  const orgValue = org ?? v?.settings.organizationId ?? "";
  const saveOrg = useMutation({
    mutationFn: () => api<ZohoBooksView>("/v1/console/integrations/zoho-books", { method: "PUT", body: { organizationId: orgValue.trim() } }),
    onSuccess: (r) => {
      set(r);
      setOrg(null);
      notify.success("Zoho Books organization saved", r.connected ? "Run Test connection, then turn auto-invoice on." : "Connect Zoho on the card above.");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const test = useMutation({
    mutationFn: () => api<ZohoBooksView>("/v1/console/integrations/zoho-books/test", { method: "POST" }),
    onSuccess: (r) => {
      set(r);
      if (r.lastTest?.ok) notify.success("Zoho Books connected", r.lastTest.message ?? undefined);
      else notify.error("Test didn't pass", r.lastTest?.message ?? undefined);
    },
    onError: (e) => notify.error("Couldn't test", message(e)),
  });
  const invoicing = useMutation({
    mutationFn: (body: Partial<ZohoBooksView["settings"]>) => api<ZohoBooksView>("/v1/console/integrations/zoho-books/invoicing", { method: "PUT", body }),
    onSuccess: (r, body) => {
      set(r);
      if (body.autoInvoice !== undefined) notify.success(r.settings.autoInvoice ? "Auto-invoice bookings is on" : "Auto-invoice bookings is off", r.settings.autoInvoice ? `Every booking is invoiced in Zoho Books at the customer's price${r.settings.emailInvoices ? " and emailed to them" : ""}.` : undefined);
      else if (body.emailInvoices !== undefined) notify.success(r.settings.emailInvoices ? "Invoices are emailed from Zoho Books" : "Invoices are not emailed");
      else notify.success("Invoice settings saved");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  async function flipAuto(on: boolean) {
    if (on && !(await confirm({ title: "Auto-invoice every booking?", body: "Each shipment booked in the portal or the console gets an invoice in Zoho Books at the customer's sell price (their markup, staff overrides included), the PDF is stored on the invoice, and, when emailing is on, Zoho Books emails it to the customer's billing contact. Nothing else is left for staff to do.", confirmLabel: "Turn on auto-invoice" }))) return;
    invoicing.mutate({ autoInvoice: on });
  }
  const s = v?.settings;
  return (
    <Section
      title="Zoho Books"
      description="Invoices for bookings: created in Zoho Books at the customer's price, emailed from Zoho Books, and stored in the customer's portal. Keys come from the Zoho card; this card holds the Books organization and the invoice layout."
      actions={v ? <StatusBadge domain="integration" value={v.status} /> : null}
    >
      <IntegrationBody q={q} canManage={canManage}>
        {v && s ? (
          <>
            {!v.connected && v.source !== "env" ? <Banner tone="info">Connect Zoho on the card above first.{v.source === "mock" ? " Until then invoices are only recorded here (mock mode)." : ""}</Banner> : null}
            <DefinitionList
              items={[
                { term: "Books organization", detail: <span className="ffx-num">{s.organizationId || "Not set"}</span> },
                { term: "Keys", detail: v.source === "console" ? "Shared Zoho connection" : v.source === "env" ? "Server environment (ZOHO_BOOKS_*)" : "None (mock: nothing leaves this server)" },
                { term: "Last test", detail: testLine(v.lastTest) },
                { term: "Payment terms", detail: s.paymentTermsDays === 0 ? "Due on receipt" : `Net ${s.paymentTermsDays}` },
                { term: "Invoice line", detail: s.itemName },
                { term: "Emailing", detail: s.emailInvoices ? "Emailed to the customer's billing contact from Zoho Books" : "Not emailed (marked sent in Zoho Books)" },
              ]}
            />
            {canManage ? (
              <>
                <form
                  className="ffa-inlineform"
                  autoComplete="off"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (/^\d{1,20}$/.test(orgValue.trim())) saveOrg.mutate();
                  }}
                >
                  <TextField label="Zoho Books organization ID" value={orgValue} onChange={(e) => setOrg(e.target.value)} inputMode="numeric" spellCheck={false} hint="Zoho Books > Settings > Organization Profile" />
                  <Button type="submit" size="lg" variant="primary" disabled={!/^\d{1,20}$/.test(orgValue.trim()) || orgValue.trim() === s.organizationId} loading={saveOrg.isPending}>Save</Button>
                </form>
                <div className="ffa-btnrow">
                  <Button disabled={!v.configured} loading={test.isPending} onClick={() => test.mutate()}>Test connection</Button>
                  <Button onClick={() => setLayoutOpen(true)}>Invoice layout</Button>
                </div>
                <Toggle
                  look="switch"
                  label="Auto-invoice bookings"
                  description={s.autoInvoice ? "On. Every booking is invoiced in Zoho Books." : v.configured && v.lastTest?.ok !== true ? "Run a passing test first." : "Off. Invoices are issued on delivery and pushed by hand."}
                  checked={s.autoInvoice}
                  disabled={invoicing.isPending || (!s.autoInvoice && v.configured && v.lastTest?.ok !== true)}
                  onChange={(on) => void flipAuto(on)}
                />
                <Toggle look="switch" label="Email invoice to customer from Zoho Books" checked={s.emailInvoices} disabled={invoicing.isPending || !s.autoInvoice} description={s.autoInvoice ? "To the customer's billing users (else the person who booked)." : "Only with auto-invoice on."} onChange={(on) => invoicing.mutate({ emailInvoices: on })} />
              </>
            ) : null}
          </>
        ) : null}
      </IntegrationBody>
      {layoutOpen && s ? <ZohoLayoutDialog settings={s} onClose={() => setLayoutOpen(false)} onSave={(body) => invoicing.mutate(body)} /> : null}
      {dialog}
    </Section>
  );
}

function ZohoLayoutDialog({ settings: s, onClose, onSave }: { settings: ZohoBooksView["settings"]; onClose: () => void; onSave: (body: Partial<ZohoBooksView["settings"]>) => void }) {
  const [f, setF] = useState({ paymentTermsDays: String(s.paymentTermsDays), itemName: s.itemName, notes: s.notes, terms: s.terms, po: s.customFields.po, consignee: s.customFields.consignee, shipper: s.customFields.shipper, emailSubject: s.emailSubject, emailBody: s.emailBody });
  const terms = Number(f.paymentTermsDays);
  const valid = Number.isInteger(terms) && terms >= 0 && terms <= 120 && f.itemName.trim().length > 0;
  return (
    <Modal
      open
      onClose={onClose}
      title="Invoice layout"
      description="What every auto-invoice says. Defaults match the sample invoice: one “LTL Shipping” line at the sell price, Net 15, the notes and the terms & conditions; consignee, shipper and the customer's P.O.# go in the notes."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!valid}
            onClick={() => {
              onSave({ paymentTermsDays: terms, itemName: f.itemName.trim(), notes: f.notes, terms: f.terms, customFields: { po: f.po.trim(), consignee: f.consignee.trim(), shipper: f.shipper.trim() }, emailSubject: f.emailSubject.trim(), emailBody: f.emailBody });
              onClose();
            }}
          >
            Save layout
          </Button>
        </>
      }
    >
      <div className="ffx-fields">
        <TextField label="Payment terms (days)" inputMode="numeric" value={f.paymentTermsDays} onChange={(e) => setF({ ...f, paymentTermsDays: e.target.value })} hint="0 = due on receipt. A customer's own terms or approved credit terms win." error={valid || f.paymentTermsDays === "" ? undefined : "0 to 120 days"} />
        <TextField label="Invoice item name" value={f.itemName} onChange={(e) => setF({ ...f, itemName: e.target.value })} maxLength={100} />
        <TextAreaField className="ffx-span" label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} maxLength={500} />
        <TextAreaField className="ffx-span" label="Terms & conditions" value={f.terms} onChange={(e) => setF({ ...f, terms: e.target.value })} rows={4} maxLength={2000} />
        <TextField label="Zoho custom field for P.O.#" optional value={f.po} onChange={(e) => setF({ ...f, po: e.target.value })} hint="Label of a custom field you created in Zoho Books; blank = notes only." />
        <TextField label="Custom field for Consignee" optional value={f.consignee} onChange={(e) => setF({ ...f, consignee: e.target.value })} />
        <TextField label="Custom field for Shipper" optional value={f.shipper} onChange={(e) => setF({ ...f, shipper: e.target.value })} />
        <TextField label="Email subject" optional value={f.emailSubject} onChange={(e) => setF({ ...f, emailSubject: e.target.value })} hint="Blank = Zoho Books' own invoice email template." />
        <TextAreaField className="ffx-span" label="Email body" optional value={f.emailBody} onChange={(e) => setF({ ...f, emailBody: e.target.value })} rows={3} maxLength={5000} />
      </div>
    </Modal>
  );
}
